import React, { useRef, useEffect, useState, useMemo } from "react";
import { Editor } from "@tinymce/tinymce-react";
import Skeleton from "react-loading-skeleton";
import { sequenceNumber } from "./../../../revampCRUD/ticketsNforms.crud";
import customToast from "../../../components/common/basic/Toast.js";
import { uploadFile } from "../../../revampCRUD/uploads.crud";
import { cleanEmailHTML, cleanForTinyMCE, sanitizeHTML } from "./../utils/tinymce.cleaner";

export default function TinyMCEEngine({
    value = "",
    onChange,
    placeholder = "",
    height = 300,
    type = "text",
    config = {},
    className,
    variables = false, // flag to show Variables menu
    disabled = false,
    sanitize = true,
    ...rest
}) {
    const editorRef = useRef(null);
    const lastProcessedRef = useRef("");
    const initializedRef = useRef(false);

    const [sequenceNumbers, setSequenceNumbers] = useState({});
    const [loading, setLoading] = useState(false);
    const [seqLoaded, setSeqLoaded] = useState(false);
    const [internalValue, setInternalValue] = useState("");

    // Base placeholder map - computed dynamically
    const baseVariables = useMemo(() => {
        const vars = {
            Name: "name",
            Email: "email_id",
            "Phone Number": "phone_number",
            "Booking Id": "booking_id",
            "Ticket Name": "ticket_name",
            "Event URL": "event_url",
            "Event Name": "event_name",
            "Organiser Email": "organiser_email",
            "Ticket URL": "ticket_url",
            ...(config.leadFollowupEmail === false && { "QR Code": "qrcode" }),
            "Refund Amount": "refund_amount",
            "Booking Date": "registered_at",
            "Organizer Name": "organiser_name",
            "Event Date": "event_date",
            Venue: "venue",
            "Venue URL": "venue_url",
            "Payment Link": "payment_link"
        };

        if (placeholder === "Your Referral Email text here" || placeholder === "Advanced Template") {
            vars["Referral link"] = "referral_link";
        }

        return vars;
    }, [placeholder, config.leadFollowupEmail]);

    const getSequenceNumber = async () => {
        if (!config?.eventId) return;
        setLoading(true);
        try {
            const res = await sequenceNumber(config?.eventId);
            if (res.status === 200) {
                const seqNumDetailsFromApi = res.data.sequence_number_details;
                const seqDataForApi = [];
                const seqSetter = {};

                seqNumDetailsFromApi?.forEach((obj) => {
                    if (
                        obj?.attached_tickets?.some((each) =>
                            rest?.formik?.values?.applicable_ticket_ids
                                ?.map((each) => Number(each))
                                ?.includes(Number(each))
                        ) ||
                        obj?.attached_tickets?.includes(rest?.formik?.values?.ticket_id)
                    ) {
                        const seqName = obj?.sequence_name?.toLowerCase().replace(" ", "_");
                        seqSetter[obj?.sequence_name] = `{${seqName}}`;
                        seqDataForApi.push({
                            name: `${seqName}`,
                            value: `seq_${obj?.sequence_id.replaceAll("-", "_")}`,
                        });
                    }
                });
                setSequenceNumbers(seqSetter);
                if (config?.handleSeqDataFromChild) config?.handleSeqDataFromChild(seqDataForApi);
            }
        } catch (err) {
            const errorMessage = err?.message || err?.response?.data?.error || "Something went wrong";
            customToast.error(errorMessage);
        } finally {
            setLoading(false);
            setSeqLoaded(true);
        }
    };

    const handleImageUpload = async file => {
        try {
            const downloadUrl = await uploadFile(file, 4);
            return downloadUrl;
        } catch (error) {
            console.error("Upload failed:", error);
            throw error;
        }
    };

    // Value cleanup and sync
    const handleEditorChange = (content, editor) => {
        if (content === lastProcessedRef.current) return;

        // your existing logic ↓
        let cleanHtml = content
            .replaceAll(`<div style=margin-top="5%">`, "<div>")
            .replaceAll("white-space: pre-wrap", `white-space: initial`)
            .replaceAll("white-space: pre;", `white-space: initial`)
            .replaceAll("white-space: pre-wrap;", `white-space: initial`);

        const regex = /<img((?!width=)[^>])*?>/g;
        cleanHtml = cleanHtml.replace(regex, (match) => {
            if (!match.includes("width=")) {
                return match.replace(/<img/, '<img width="700"');
            }
            return match;
        });

        cleanHtml = cleanHtml.replace(
            /<p[^>]*>\s*(<img[^>]+>)\s*<\/p>/g,
            '<div class="responsive-image-wrapper">$1</div>'
        ).replaceAll(
            "${qrcode}",
            `https://files.konfhub.com/sample_qrcode.png`
        );
        const prettyHtml = cleanHtml;
        if (prettyHtml !== internalValue) {
            lastProcessedRef.current = prettyHtml;
            setInternalValue(prettyHtml);
        }

        if (type === "email") cleanHtml = cleanEmailHTML(cleanHtml);
        else if (sanitize) cleanHtml = sanitizeHTML(cleanHtml);

        const rawHtml = cleanHtml;

        if (typeof onChange === "function" && value !== rawHtml)
            onChange(rawHtml, editor);
    };

    const readyForInit =
        !loading &&
        seqLoaded &&
        value !== undefined;       // value MUST exist, empty or not

    useEffect(() => {
        if (!readyForInit) return;
        if (initializedRef.current) return;

        const incomingRaw = value ?? ""; // if null → "", if "" → "", if API data → good
        const incoming = sanitize ? sanitizeHTML(incomingRaw) : incomingRaw;

        const finalHtml = cleanForTinyMCE(incoming)

        setInternalValue(finalHtml);

        lastProcessedRef.current = finalHtml;
        initializedRef.current = true;
    }, [readyForInit, value]);

    useEffect(() => {
        if (
            placeholder === "Your Contact Attendee Mail text" ||
            placeholder === "Advanced Template"
        ) {
            if (variables) getSequenceNumber();
        } else {
            setSeqLoaded(true); // no seq needed
        }
    }, [rest?.formik?.values?.applicable_ticket_ids, rest?.formik?.values?.ticket_id]);

    // Build TinyMCE init config
    const initConfig = {
        menubar: false,
        height,
        min_height: height,
        max_height: 700,
        placeholder,
        branding: false,
        paste_data_images: false,
        paste_enable_default_filters: false,
        paste_as_text: config.pasteAsPlainText ?? false,
        forced_root_block_attrs: {
            style: "font-family: Hind, sans-serif;",
        },
        content_style: `
            body * { margin: 0; font-family: Hind, sans-serif; }
            .responsive-image-wrapper img { max-width: 100%; height: auto; display: block; margin: auto; }
        `,
        plugins: config.plugins,
        toolbar: config.toolbar,
        images_upload_handler: async (blobInfo) => {
            try {
                const file = new File([blobInfo.blob()], blobInfo.filename(), {
                    type: blobInfo.blob().type,
                });
                const url = await handleImageUpload(file);
                return {
                    html: `<div class="responsive-image-wrapper"><img src="${url}" alt="${blobInfo.filename()}" style="max-width:100%;height:auto;display:block;" /></div>`,
                };
            } catch (error) {
                throw new Error("Image upload failed");
            }

        },
        file_picker_callback: (callback, _value, meta) => {
            if (meta.filetype === "image") {
                const input = document.createElement("input");
                input.setAttribute("type", "file");
                input.setAttribute("accept", "image/*");
                input.addEventListener("change", async () => {
                    const file = input.files[0];
                    try {
                        const downloadUrl = await handleImageUpload(file);
                        callback(downloadUrl, { alt: file.name, width: 700 });
                    } catch {
                        callback("", { alt: "Image upload failed" });
                    }
                });
                input.click();
            }
        },
        paste_preprocess: (plugin, args) => {
            args.content = cleanForTinyMCE(args.content);
        },
        setup: (editor) => {
            window.tester = editor;
            editor.pasteAsPlainText = config.pasteAsPlainText ?? false;

            // font family menu
            const fontOptions = [
                { title: "Hind", value: "Hind, sans-serif" },
                { title: "Arial", value: "Arial, sans-serif" },
                { title: "Georgia", value: "Georgia, serif" },
                { title: "Courier New", value: "'Courier New', monospace" },
                { title: "Verdana", value: "Verdana, sans-serif" },
                { title: "Times New Roman", value: "'Times New Roman', serif" },
            ];
            editor.ui.registry.addMenuButton("fontfamily", {
                text: "Font Family",
                fetch: (cb) =>
                    cb(
                        fontOptions.map((opt) => ({
                            type: "menuitem",
                            text: opt.title,
                            onAction: () => {
                                editor.execCommand("fontName", false, opt.value);
                                editor.dom.setStyle(editor.selection.getNode(), "font-family", opt.value);
                            },
                        }))
                    ),
            });

            // font size menu
            const fontSizeOptions = [
                { title: "Small", value: "12px" },
                { title: "Normal", value: "14px" },
                { title: "Large", value: "18px" },
                { title: "Huge", value: "24px" },
            ];
            editor.ui.registry.addMenuButton("fontsize", {
                text: "Font Size",
                fetch: (cb) =>
                    cb(
                        fontSizeOptions.map((opt) => ({
                            type: "menuitem",
                            text: opt.title,
                            onAction: () => {
                                editor.execCommand("fontSize", false, opt.value);
                                editor.dom.setStyle(editor.selection.getNode(), "font-size", opt.value);
                            },
                        }))
                    ),
            });

            // ✅ Variables menu (controlled via flag)
            if (variables) {
                editor.ui.registry.addMenuButton("variables", {
                    title: "Insert Variables",
                    text: "Insert Variables",
                    fetch: (callback) => {
                        const vars = { ...baseVariables, ...config.variables };
                        const items = Object.keys(vars).map((key) => ({
                            type: "menuitem",
                            text: key,
                            onAction: () => {
                                if (key === "QR Code") {
                                    editor.execCommand(
                                        "mceInsertContent",
                                        false,
                                        `<div style="text-align:center;" class="image">
                                            <img src="https://files.konfhub.com/sample_qrcode.png" width="200" height="auto" />
                                        </div>`
                                    );
                                } else {
                                    editor.execCommand("mceInsertContent", false, `$\{${vars[key]}}`);
                                }
                            },
                        }));

                        if (Object.keys(sequenceNumbers).length > 0) {
                            items.push({
                                type: "nestedmenuitem",
                                text: "Sequence Numbers",
                                getSubmenuItems: () =>
                                    Object.keys(sequenceNumbers).map((subKey) => ({
                                        type: "menuitem",
                                        text: subKey,
                                        onAction: () =>
                                            editor.execCommand(
                                                "mceInsertContent",
                                                false,
                                                `$\{${sequenceNumbers[subKey]}}`
                                            ),
                                    })),
                            });
                        }

                        callback(items);
                    },
                });
            }

            if (typeof config.setup === "function") config.setup(editor);
        },
        ...config.initOverrides, // allow low-level overrides
    };

    if (!seqLoaded) {
        return (
            <div style={{ padding: "10px" }}>
                {/* Toolbar skeleton */}
                <Skeleton height={38} borderRadius={6} style={{ marginBottom: "8px" }} />

                {/* Content skeleton */}
                <Skeleton height={height - 40} borderRadius={6} />
            </div>
        );
    }

    return (
        <Editor
            tinymceScriptSrc="/tinymce/tinymce.min.js"
            licenseKey="gpl"
            init={initConfig}
            value={internalValue}
            disabled={disabled}
            onEditorChange={handleEditorChange}
            onInit={(_evt, editor) => (editorRef.current = editor)}
            className={className}
            {...rest}
        />
    );
};